import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import ConfigurationAutomationGrossingShelfImmediateShelfPriorityModal
    from '@/modules/configurationModule/detail/automation/grossing-shelf/immediate-shelf-priority/components/modal/configuration-automation-grossing-shelf-immediate-shelf-priority-modal.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import ModalWrapper from '@/components/ModalWrapper.vue'
import {
    ConfigurationAutomationGrossingShelfImmediateShelfPriorityGridState,
    initialConfigurationAutomationGrossingShelfImmediateShelfPriorityGridState
} from '@/modules/configurationModule/detail/automation/grossing-shelf/immediate-shelf-priority/components/grid/configuration-automation-grossing-shelf-immediate-shelf-priority-grid.model'
import { GetContextMenuItemsParams, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import {
    ConfigurationImmediateShelfPriorityAssignmentCreateRequest,
    ConfigurationImmediateShelfPriorityAssignmentResponse
} from '@/modules/configurationModule/detail/automation/grossing-shelf/immediate-shelf-priority/services/configuration-immediate-shelf-priority-assignment/configuration-immediate-shelf-priority-assignment.dto'
import { EntityIdentifiersRequest, EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'

@Component({
    components: {
        ConfigurationSectionHeader,
        VtGrid,
        ConfigurationAutomationGrossingShelfImmediateShelfPriorityModal
    }
})
export default class ConfigurationAutomationGrossingShelfImmediateShelfPriorityGrid extends BaseCrudServerSideComponent<ConfigurationImmediateShelfPriorityAssignmentResponse> implements BaseCrud {
    @Ref() readonly immediateShelfPriorityModalWrapper!: typeof ModalWrapper

    state: ConfigurationAutomationGrossingShelfImmediateShelfPriorityGridState

    get canDelete(): boolean {
        return !!this.state.selectedRows.length
    }

    constructor() {
        super()
        this.state = initialConfigurationAutomationGrossingShelfImmediateShelfPriorityGridState()
    }

    /// SERVICES CALLS ///

    private async _createImmediateShelfPriorityAssignment(createRequest: ConfigurationImmediateShelfPriorityAssignmentCreateRequest): Promise<ConfigurationImmediateShelfPriorityAssignmentResponse | null> {
        const response = await this.$_toCallUnique(this.state.service.create(this.state.laboratoryId, createRequest))
        if (response) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            return response
        }
        return null
    }

    private async _deleteImmediateShelfPriorityAssignments(requestDelete: EntityIdentifiersRequest): Promise<EntityIdentifierWithErrorResponse[] | null> {
        const ids = requestDelete?.entityIdentifiers
        if (ids?.length) {
            const response = await this.$_toCallUnique(this.state.service.delete(this.state.laboratoryId, requestDelete))
            if (response) {
                const elements = ids.length - response.length
                if (elements > 0) {
                    const choice = elements > 1 ? 2 : 1
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted', choice, { elements }))
                }
                return response
            }
        }
        return null
    }

    /// GRID OPERATIONS ///

    async createOperation(createRequest: ConfigurationImmediateShelfPriorityAssignmentCreateRequest): Promise<void> {
        if (createRequest) {
            if (await this._createImmediateShelfPriorityAssignment(createRequest)) {
                this.reloadData()
            }
        }
    }

    private async _showModalAdviceWhenDelete(): Promise<boolean> {
        const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
        const selectedRowsSize: number = this.state?.selectedRows?.length
        const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
        const labelQuestion: string = this.$tc('configuration.grid.deleteQuestion')
        const labelAcceptAction: string = this.$tc('configuration.actions.delete')
        return this.$_showConfirmationModal(
            `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelQuestion}`,
            labelAcceptAction
        )
    }

    async deleteOperation(requestDelete: EntityIdentifiersRequest): Promise<void> {
        if (requestDelete) {
            if (await this._showModalAdviceWhenDelete()) {
                if (await this._deleteImmediateShelfPriorityAssignments(requestDelete)) {
                    this.reloadData()
                }
            }
        }
    }

    /// GRID EVENT HANDLERS ///

    async onDelete(): Promise<void> {
        if (this.state.selectedRows.length) {
            const requestDelete: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(immediateShelfPriorityAssignment => immediateShelfPriorityAssignment.id)
            }
            await this.deleteOperation(requestDelete)
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.immediateShelfPriorityModalWrapper)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        return [
            {
                name: this.$tc('configuration.actions.delete'),
                cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                action: this.onDelete
            }
        ]
    }
}
